class ContratoUsuarios {
    static get $inject() {
        return ['api', 'gql', '$uibModal', '$scope']
    }

    constructor(api, gql, modal, scope) {
        this.api = api
        this.gql = gql
        this.modal = modal
        this.apply = (fun) => scope.$apply(fun)
    }

    $onInit() {
        this.contratoId = this.resolve.contratoId
        this.unsubscriber = this.setupSubscription()

        this.initialPromise = this
            .loadData()
            .then(({ contrato, empresas, ferramentas, permissoes }) => {
                this.empresas = empresas
                this.usuariosContratados = contrato.usuariosContratados
                this.ferramentasContratadas = ferramentas

                this.permissoes = permissoes.sortBy(p => p.usuario.nome).sortBy(p => p.usuario.role !== 'consultor')
                this.permissoesUsuariosConsultores = permissoes.filter(p => p.usuario.role === 'consultor')
            })
    }

    $onDestroy() {
        this.unsubscriber()
    }

    setupSubscription() {
        const subscription = `
            subscription {
                permissaoCadastrada(empresaId: ${this.contratoId}) {
                    id
                    admin
                    ferramentas
                    usuario {
                        id
                        cpf
                        nome
                        role
                    }
                    empresas: filiais {
                        id
                        cnpj
                        razaoSocial
                        cidade {
                            id
                            nome
                            estado {
                                id
                                sigla
                            }
                        }
                    }
                }
            }
        `

        return this.gql.subscribe(subscription, {}, data => this.apply(() => this.onEvent(data)))
    }

    onEvent({ data: { permissaoCadastrada } }) {
        if (permissaoCadastrada) {
            this.permissoes.push(this.formatPermissao(permissaoCadastrada, this.ferramentasContratadas))

            Alert.success(`O usuário ${permissaoCadastrada.usuario.nome} foi vinculado na empresa.`, 'Sucesso!')
        }
    }

    loadData() {
        const query = gql`
            query EmpresaUsuarios($contratoId: Int!) {
                contrato: empresa(id: $contratoId) {
                    id
                    usuariosContratados 
                    empresas: filiais {
                        id
                        cnpj
                        razaoSocial
                        ferramentasContratadas
                        cidade {
                            id
                            nome
                            estado {
                                id
                                sigla
                            }
                        }
                    }
                    permissoes {
                        id
                        admin
                        ferramentas
                        usuario {
                            id
                            nome
                            cpf
                            role
                        }
                        empresas: filiais {
                            id
                            cnpj
                            razaoSocial
                            cidade {
                                id
                                nome
                                estado {
                                    id
                                    sigla
                                }
                            }
                        }
                    }
                }
            }
        `

        return this.gql
            .query(query, { contratoId: parseInt(this.contratoId) })
            .then(({ contrato: { empresas, permissoes }, contrato }) => {
                empresas = empresas.map(this.formatEmpresa)

                const ferramentasContratadas = empresas
                    .flatMap(empresa => empresa.ferramentasContratadas)
                    .uniq()

                const ferramentas = this.resolve.ferramentasDisponiveis.filter(f => ferramentasContratadas.includes(f.id))

                permissoes = permissoes.map(permissao => this.formatPermissao(permissao, ferramentas))

                return {
                    contrato,
                    empresas,
                    ferramentas,
                    permissoes
                }
            })
    }

    formatEmpresa(empresa) {
        return {
            ...empresa,
            id: parseInt(empresa.id),
            cidade: empresa.cidade.nome,
            estado: empresa.cidade.estado.sigla
        }
    }

    formatPermissao(permissao, ferramentas) {
        return {
            ...permissao,
            id: parseInt(permissao.id),
            empresas: permissao.empresas.map(this.formatEmpresa),
            ferramentas: permissao.ferramentas.filter(ferramentaId => 
                ferramentas.find(ferramenta => ferramenta.id === ferramentaId)
            )
        }
    }

    openAdicionarUsuarioModal() {
        this.modal.open({
            component: 'adicionarUsuario',
            size: 'lg',
            backdrop: false,
            keyboard: 'static',
            resolve: {
                cpf: () => this.cpf,
                contratoId: () => this.contratoId
            }
        })
    }

    adicionar(form) {
        if (form.$valid) {
            const query = gql`
                query Usuario($cpf: String!) {
                    usuario(cpf: $cpf) {
                        id
                    }
                }
            `

            this.gql.query(query, { cpf: this.cpf })
                .then(({ usuario }) => {
                    if (usuario) {
                        if (this.permissoes.some(p => p.usuario.id == usuario.id)) {
                            Alert.warning("O CPF informado já se encontra vinculado a esta empresa.", "Atenção")
                        } else {
                            const mutation = gql`
                                mutation CreatePermissao {
                                    permissao: createPermissao(empresaId: ${this.contratoId}, usuarioId: ${parseInt(usuario.id)}) {
                                        id
                                        admin
                                        ferramentas
                                        usuario {
                                            id
                                            nome
                                            cpf
                                            role
                                        }
                                        empresas: filiais {
                                            id
                                            cnpj
                                            razaoSocial
                                            cidade {
                                                id
                                                nome
                                                estado {
                                                    id
                                                    sigla
                                                }
                                            }
                                        }
                                    }
                                }
                            `

                            return this.gql.mutate(mutation, {})
                                .then(({ permissao }) => {
                                    this.apply(() => {
                                        this.permissoes.push(this.formatPermissao(permissao, this.ferramentasContratadas))
                                        this.cpf = null
                                        form.$setPristine()
                                    })
                                })
                        }
                    } else {
                        this.openAdicionarUsuarioModal()
                    }
                })
        }
    }

    desvincular(permissao) {
        Confirm.warning(
            'Atenção',
            `Deseja realmente desvincular o usuário ${permissao.usuario.nome} da empresa?`,
            {
                'Não': angular.noop,
                'Sim': () => {
                    const mutation = gql`
                        mutation DesvincularUsuario {
                            permissao: deletePermissao(permissaoId: ${permissao.id}) {
                                id
                            }
                        }
                    `

                    this.gql.mutate(mutation, {})
                        .then(({ permissao }) => {
                            this.apply(() => {
                                this.permissoes = this.permissoes.reject(p => p.id == permissao.id)
                                Alert.success('O usuário foi desvinculado com sucesso.', 'Sucesso!')
                            })
                        })
                        .catch(([{ message }]) => {
                            if (message === 'last_admin') {
                                Alert.warning('A empresa precisa de pelo menos 1 usuário administrador.', 'Operação inválida!')
                            }
                        })
                }
            }
        )
    }
}

angular.module("eficienciaFiscalModule")
    .config(function ($qProvider) {
        $qProvider.errorOnUnhandledRejections(false)
    })
    .component("contratoUsuarios", {
        controller: ContratoUsuarios,
        bindings: {
            close: '&',
            resolve: '<'
        },
        template: `
        <modal modal-title="Usuários da empresa" close="$ctrl.close">
            <modal-body>
                <div class="clearfix">
                    <loading promise="$ctrl.initialPromise">
                        <div ng-if="$ctrl.permissoesUsuariosConsultores.length >= $ctrl.usuariosContratados">
                            <h5 style="text-align: center; padding: 15px;">Limite de usuários contratados atingido. 
                                <a href="javascript:Intercom('show')"> Clique aqui</a> para falar com um de nossos consultores.
                            </h5>
                        </div> 
                        <form ng-if="$ctrl.permissoesUsuariosConsultores.length < $ctrl.usuariosContratados" ng-submit="$ctrl.adicionar(adicionarCpf)" name="adicionarCpf" novalidate="novalidate" class="row">
                            <div class="form-group col-xs-9">
                                <label for="cpf">CPF do usuário</label>
                                <input
                                    type="text"
                                    class="form-control"
                                    placeholder="000.000.000-00"
                                    ng-model="$ctrl.cpf"
                                    name="cpf"
                                    ui-br-cpf-mask="ui-br-cpf-mask"
                                    required/>
                                <div 
                                    ng-messages="adicionarCpf.cpf.$error"
                                    ng-messages-multiple="ng-messages-multiple"
                                    ng-messages-include="erros-validacao"
                                    ng-if="adicionarCpf.$submitted">
                                    <span ng-message="cpf" class="help-block txt-color-red font-xs no-margin">CPF inválido</span>
                                </div>
                            </div>
                            <div class="col-xs-3">
                                <button
                                    type="submit" ng-disabled="$ctrl.submitAdicionarTracker.active()" class="btn btn-primary btn-block no-label">
                                    <span ng-hide="$ctrl.submitAdicionarTracker.active()">
                                        <i class='fa fa-plus'></i> Vincular
                                    </span>
                                    <span ng-show="$ctrl.submitAdicionarTracker.active()">
                                        <i class="fa fa-circle-o-notch fa-spin"></i> Vinculando...
                                    </span>
                                </button>
                            </div>
                        </form>
                    </loading>

                    <div ng-if="$ctrl.permissoes">
                        <highlighted ng-if="!$ctrl.permissoes.length">
                            <main>A empresa atual não possui nenhum usuário vinculado.</main>
                        </highlighted>
                        
                        <contrato-usuario-details 
                            ng-repeat="permissao in $ctrl.permissoes" 
                            empresas="$ctrl.empresas"
                            ferramentas="$ctrl.ferramentasContratadas"
                            permissao="permissao"
                            on-desvincular="$ctrl.desvincular(permissao)">
                        </contrato-usuario-details>
                    </div>    
                </div>
            </modal-body>
            <modal-footer>
                <button type="button" ng-click="$ctrl.close()" class="btn btn-default">Fechar</button>
            </modal-footer>
        </modal>
        `
    })
